import type { ActivityLevel } from "@/libs/tracking/weekProfileSummary";

export interface ActivityLevelInput {
  postsInPeriod: number;
  noPostsInLast7Days: boolean;
  mediaLikes: number;
  likedUsersCount: number;
  avgLikesPerPost: number;
  /** Sum of gained/lost followers and following in the period. */
  socialChanges?: number;
}

/**
 * Heuristic activity tier for the summary card.
 *
 * Posting in the last 7 days is required for "very active".
 * Accounts with no recent posts can still be "moderately active" when
 * older analyzed posts show meaningful like volume.
 */
export function resolveActivityLevel(input: ActivityLevelInput): ActivityLevel {
  const {
    postsInPeriod,
    noPostsInLast7Days,
    mediaLikes,
    likedUsersCount,
    avgLikesPerPost,
    socialChanges = 0,
  } = input;

  if (
    mediaLikes === 0 &&
    likedUsersCount === 0 &&
    postsInPeriod === 0 &&
    socialChanges === 0
  ) {
    return "barely active";
  }

  if (noPostsInLast7Days) {
    if (
      mediaLikes >= 40 ||
      likedUsersCount >= 20 ||
      avgLikesPerPost >= 15
    ) {
      return "moderately active";
    }
    if (mediaLikes >= 5 || likedUsersCount >= 3 || avgLikesPerPost >= 3) {
      return "moderately active";
    }
    return "barely active";
  }

  if (
    postsInPeriod >= 4 ||
    (postsInPeriod >= 2 && mediaLikes >= 30) ||
    (postsInPeriod >= 2 && likedUsersCount >= 15) ||
    mediaLikes >= 100 ||
    (mediaLikes >= 50 && likedUsersCount >= 20)
  ) {
    return "very active";
  }

  if (
    postsInPeriod >= 1 ||
    mediaLikes >= 10 ||
    likedUsersCount >= 5 ||
    avgLikesPerPost >= 5 ||
    socialChanges >= 5
  ) {
    return "moderately active";
  }

  return "barely active";
}

export function activityLevelLabel(level: ActivityLevel): string {
  switch (level) {
    case "very active":
      return "Very active";
    case "moderately active":
      return "Moderately active";
    case "barely active":
      return "Barely active";
  }
}
